/**
 * EditColumnDialog
 *
 * Dialog per modificare nome e colore di una colonna Kanban.
 */
import { useState, useEffect } from "react";
import { Loader2, Check } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";

// Palette di colori predefiniti per le colonne
const COLUMN_COLORS = [
  { name: "Blu", value: "#2b7fff" },
  { name: "Viola", value: "#7b3fff" },
  { name: "Verde", value: "#00d492" },
  { name: "Arancione", value: "#fe9a00" },
  { name: "Rosso", value: "#ef4444" },
  { name: "Ciano", value: "#06b6d4" },
  { name: "Rosa", value: "#ec4899" },
  { name: "Giallo", value: "#eab308" },
  { name: "Indaco", value: "#6366f1" },
  { name: "Turchese", value: "#14b8a6" },
];

export interface EditColumnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnId?: number;
  columnName?: string;
  columnColor?: string;
  /** Callback per salvare le modifiche */
  onSave: (columnId: number, name: string, color: string) => Promise<{ success: boolean; error?: string }>;
}

export default function EditColumnDialog({
  open,
  onOpenChange,
  columnId,
  columnName: initialColumnName = "",
  columnColor: initialColumnColor = "#7b3fff",
  onSave,
}: EditColumnDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState(initialColumnName);
  const [color, setColor] = useState(initialColumnColor);
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);

  // Reset allo stato iniziale quando cambia il dialog
  useEffect(() => {
    if (open) {
      setName(initialColumnName);
      setColor(initialColumnColor);
      setError(null);
      setShowCustomColorPicker(false);
    }
  }, [open, initialColumnName, initialColumnColor]);

  const isValid = name.trim().length > 0 && color.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (columnId === undefined || submitting || !isValid) return;

    setError(null);
    setSubmitting(true);

    try {
      const result = await onSave(columnId, name.trim(), color);
      if (result.success) {
        onOpenChange(false);
      } else {
        setError(result.error || "Errore durante il salvataggio");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const isCustomColor = !COLUMN_COLORS.some(c => c.value === color);

  return (
    <AlertDialog open={open} onOpenChange={(o) => !submitting && onOpenChange(o)}>
      <AlertDialogContent className="bg-[var(--hexa-surface)] border-[var(--hexa-border)] max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-[var(--hexa-text-primary)]">
            Modifica colonna
          </AlertDialogTitle>
          <AlertDialogDescription className="text-[var(--hexa-text-secondary)]">
            Modifica il nome e il colore della colonna.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-2">
            {/* Nome colonna */}
            <div>
              <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-2">
                Nome colonna
                <span className="text-[#ff6467]">*</span>
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError(null);
                }}
                disabled={submitting}
                placeholder="Es: In corso, Da fare..."
                className="w-full rounded-lg bg-[var(--hexa-input-bg)] border border-[var(--hexa-input-border)] text-[var(--hexa-text-primary)] px-3 py-2 text-sm focus:outline-none focus:border-[var(--hexa-purple)] transition disabled:opacity-60 disabled:cursor-not-allowed"
                maxLength={50}
              />
            </div>

            {/* Colore */}
            <div>
              <label className="block text-xs font-medium text-[var(--hexa-text-primary)] mb-2">
                Colore
              </label>

              {/* Palette predefinita */}
              <div className="grid grid-cols-5 gap-2 mb-3">
                {COLUMN_COLORS.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => {
                      setColor(c.value);
                      setShowCustomColorPicker(false);
                      setError(null);
                    }}
                    disabled={submitting}
                    className={`w-10 h-10 rounded-full border-2 transition-all duration-200 ${
                      color === c.value
                        ? "border-[var(--hexa-text-primary)] scale-110"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: c.value }}
                    title={c.name}
                    aria-label={`Seleziona colore ${c.name}`}
                  >
                    {color === c.value && (
                      <Check className="h-4 w-4 text-white mx-auto" strokeWidth={3} />
                    )}
                  </button>
                ))}
              </div>

              {/* Colore custom */}
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setShowCustomColorPicker(!showCustomColorPicker)}
                  disabled={submitting}
                  className="flex items-center gap-2 text-xs text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] transition"
                >
                  <div
                    className="w-5 h-5 rounded border border-[var(--hexa-border)]"
                    style={{ backgroundColor: isCustomColor ? color : undefined }}
                  />
                  {isCustomColor ? "Colore custom" : "Colore custom..."}
                </button>

                {showCustomColorPicker && (
                  <div className="flex items-center gap-2 flex-1">
                    <input
                      type="color"
                      value={color}
                      onChange={(e) => {
                        setColor(e.target.value);
                        setError(null);
                      }}
                      disabled={submitting}
                      className="w-8 h-8 rounded cursor-pointer"
                    />
                    <span className="text-xs text-[var(--hexa-text-secondary)]">{color}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {error && (
            <p className="text-xs text-[#ef4444] bg-[rgba(239,68,68,0.08)] border border-[rgba(239,68,68,0.2)] rounded-md px-3 py-2">
              {error}
            </p>
          )}

          <AlertDialogFooter className="pt-4">
            <AlertDialogCancel
              disabled={submitting}
              className="bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-surface-hover)]"
            >
              Annulla
            </AlertDialogCancel>
            <Button
              type="submit"
              disabled={submitting || !isValid}
              className="bg-[var(--hexa-purple)] hover:bg-[var(--hexa-purple-hover)] text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {submitting ? "Salvataggio..." : "Salva modifiche"}
            </Button>
          </AlertDialogFooter>
        </form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
